import { useEffect, useState } from 'react';
import { useT } from '../i18n/locale-context';
import { ApiError } from '../lib/api';
import { useOnline } from '../lib/online-status';
import { GRID_COLS } from './tender-list';

/**
 * The search said «not now» rather than failing: its load guard refused this
 * request (`search-load-guard.ts`). `search_busy` (503) — too many searches on
 * the whole site at once; `search_too_many` (429) — this visitor's own earlier
 * searches are still running. Neither is a connection problem, and telling the
 * reader to «check the connection» sends them to fix the wrong thing.
 */
function refusalKey(error: unknown): 'error.searchBusy' | 'error.searchTooMany' | null {
  if (!(error instanceof ApiError)) return null;
  if (error.code === 'search_too_many' || error.status === 429) return 'error.searchTooMany';
  if (error.code === 'search_busy' || error.status === 503) return 'error.searchBusy';
  return null;
}

/**
 * Error state for a failed data query — a real message + a Retry button, so a
 * network/5xx failure no longer masquerades as an empty "0 results" list.
 *
 * Pass the query's `error` and a refusal by the search's load guard gets its
 * own sentence (owner, 2026-10-12: the reader must not be left thinking the
 * site is broken). Any other failure keeps the generic line.
 */
export function QueryError({ onRetry, error }: { onRetry: () => void; error?: unknown }) {
  const t = useT();
  return (
    <div
      role="alert"
      className="rounded-lg border border-edge bg-bone-surface p-8 text-center shadow-m-sm"
    >
      <p className="mb-3 text-[14px] font-semibold text-ink">
        {t(refusalKey(error) ?? 'error.title')}
      </p>
      <button
        type="button"
        onClick={onRetry}
        className="inline-flex h-11 w-full items-center justify-center rounded-md bg-brand px-4 text-[13px] font-bold text-white transition-colors hover:bg-brand-mid sm:w-auto"
      >
        {t('error.retry')}
      </button>
    </div>
  );
}

/** How long a list may load before the page says it is taking longer than usual. */
export const SLOW_LOAD_MS = 3000;

/**
 * «Ищем дольше обычного…» above a list that has been loading for more than
 * {@link SLOW_LOAD_MS}. Render it INSIDE the loading branch: it starts counting
 * when it mounts and is gone the moment the list arrives.
 *
 * Owner, 2026-10-12: a search with several filters can take 5–10 seconds, and a
 * skeleton that just keeps pulsing reads as a hung page — «чтобы клиент в
 * неведении не сидел». A first search of a new word is 1–2.5 s (measured the
 * same day), so three seconds is past every ordinary load and nobody sees this
 * on a normal one. Nothing is rendered before the delay (and nothing on the
 * server), so a fast load paints exactly what it painted before.
 *
 * Offline it says THAT instead, at once (22.10): «Сайт работает, результаты
 * появятся через несколько секунд» is false with no network — the query is
 * paused and will not even be sent until the connection returns, at which
 * point the list loads by itself and this unmounts.
 */
export function SlowLoadNotice({ delayMs = SLOW_LOAD_MS }: { delayMs?: number }) {
  const t = useT();
  const online = useOnline();
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setSlow(true), delayMs);
    return () => clearTimeout(timer);
  }, [delayMs]);
  if (!online) {
    return (
      <p
        role="status"
        className="mb-3 rounded-md border border-edge bg-bone px-4 py-2.5 text-[13px] text-ink-soft"
      >
        {t('offline.listBody')}
      </p>
    );
  }
  if (!slow) return null;
  return (
    <p
      role="status"
      className="mb-3 rounded-md border border-edge bg-bone px-4 py-2.5 text-[13px] text-ink-soft"
    >
      {t('catalog.slowLoad')}
    </p>
  );
}

/** Pulsing placeholder bar — the m16 shimmer (bone `edge` tint + `animate-pulse`). */
function Bar({ className = '' }: { className?: string }) {
  return <span className={`block animate-pulse rounded bg-edge ${className}`} />;
}

/**
 * One skeleton row — a 1:1 structural clone of `TenderRow`: the same wrapper
 * padding/borders and the same three regions (main column with status → title →
 * tags → AI line, plus the budget and deadline columns). Stacks as a card below
 * `md` and becomes the shared three-column grid at `md`+, exactly like the real
 * row, so the swap to data never moves anything.
 */
function SkeletonRow() {
  return (
    <div
      className={`flex flex-col gap-2.5 border-b border-edge-light px-4 py-4 last:border-b-0 md:grid ${GRID_COLS} md:items-start md:gap-3 md:px-5`}
    >
      {/* Main column: status, title, tags, AI line. */}
      <div className="flex min-w-0 flex-col gap-[6px]">
        <div className="flex items-center gap-2">
          <span className="h-[7px] w-[7px] shrink-0 rounded-full bg-edge" />
          <Bar className="h-[18px] w-16 !rounded-pill" />
        </div>
        <div className="flex flex-col gap-1.5">
          <Bar className="h-[15px] w-[92%] md:h-[14px]" />
          <Bar className="h-[15px] w-[58%] md:h-[14px]" />
        </div>
        <div className="flex flex-wrap gap-[5px]">
          <Bar className="h-[19px] w-20 !rounded-pill" />
          <Bar className="h-[19px] w-24 !rounded-pill" />
        </div>
        <div className="flex items-start gap-[5px]">
          <Bar className="mt-0.5 h-[15px] w-8 shrink-0 !rounded-[3px]" />
          <Bar className="h-[15px] w-[72%]" />
        </div>
      </div>

      {/* Budget: label/value row on mobile, right-aligned column at md+. */}
      <div className="flex items-baseline justify-between gap-3 border-t border-edge-light pt-2.5 md:block md:border-0 md:pt-0.5 md:text-right">
        <Bar className="h-[11px] w-14 md:hidden" />
        <span className="flex flex-col items-end gap-[3px]">
          <Bar className="h-[14px] w-24 md:h-[13.5px]" />
          <Bar className="h-[11px] w-12" />
        </span>
      </div>

      {/* Deadline: label/value row on mobile, right-aligned column at md+. */}
      <div className="flex items-baseline justify-between gap-3 md:flex-col md:items-end md:gap-[4px] md:pt-0.5 md:text-right">
        <Bar className="h-[11px] w-20" />
        <Bar className="h-[13px] w-16" />
      </div>
    </div>
  );
}

/**
 * Bone-palette loading skeleton for the tender list — replaces the text-only
 * "Загрузка…" card so loads read as content, not a stalled page. It clones the
 * real `TenderList`: one surface card with the desktop column header and rows
 * built from `SkeletonRow`, using the shared `GRID_COLS` template, so the swap
 * to real data doesn't shift layout (no CLS).
 */
export function TenderListSkeleton({ rows = 6 }: { rows?: number }) {
  return (
    <div
      className="overflow-hidden rounded-lg border border-edge bg-bone-surface shadow-m-sm"
      aria-hidden="true"
    >
      {/* Column header — desktop table only, mirrors TenderList's header row. */}
      <div className={`hidden border-b border-edge bg-bone px-5 py-[9px] md:grid ${GRID_COLS}`}>
        <Bar className="h-[11px] w-16" />
        <Bar className="ml-auto h-[11px] w-14" />
        <Bar className="ml-auto h-[11px] w-16" />
      </div>
      {Array.from({ length: rows }).map((_, i) => (
        <SkeletonRow key={i} />
      ))}
    </div>
  );
}
